import React, { memo, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import isEmpty from "lodash/isEmpty";
import head from "lodash/head";
import find from "lodash/find";
import get from "lodash/get";
import {
  getURLParamValue,
  autoScrollToError,
  gotoUrl,
  urlParam,
  checkOrderParamsIsValid,
  pathWithChannel
} from "utils/helpers";

import {
  fetchPlan,
  fetchReferralCode,
  fetchAddressLookup,
  fetchDeliverySlots,
  resetDeliverySlots,
  clearData,
  setData,
  createSelfActivationDeliveryOrder,
  changeFormField,
  selectPlan,
  validatePostalCode,
  resetPostalCodeValidation,
  setPostalCodeValidation,
  fetchPopStations
} from "ducks/actions";
import {
  makeSelectPageValidations,
  makeSelectIsFetchingData,
  makeSelectSelfActivationForm,
  makeSelectAddons,
  makeSelectPreSelectedAddons,
  makeSelectDefaultPlanAddons,
  makeSelectPayMonthlyItems,
  makeSelectPayTodayItems,
  makeSelectReferralCode,
  makeSelectDefaultPromoCodeOffers,
  makeSelectPlanName,
  // makeSelectPlanFAQ,
  makeSelectCountries,
  makeSelectStates,
  makeSelectOtherCountries,
  makeSelectPrimaryDocTypes,
  makeSelectMinimumAge,
  makeSelectDateOptions,
  makeSelectMonthOptions,
  makeSelectYearOptions,
  makeSelectFormattedSelectedNumber,
  makeSelectDeliveryMethod,
  makeSelectDeliveryMethods,
  makeSelectCombinedDeliveryDates,
  makeSelectDeliveryDates,
  makeSelectFreeDeliverySlots,
  makeSelectSpecialDeliverySlots,
  makeSelectPostalCodeOptions,
  makeSelectPersonalDetailsFormInitialValues,
  makeSelectHasAddressError,
  makeSelectEligiblePlans,
  makeSelectPlanDetails,
  makeSelectVerifyIdentityPayload,
  makeSelectSelfActivationOrderParams,
  makeSelectNumberType,
  makeSelectValidateIccId,
  makeSelectDetailsFromIccidVerification,
  makeSelectPreSelectedOrderAddons,
  makeSelectSelectedAddons,
  makeSelectConfirmedAddon,
  makeSelectOrderCreationStatus,
  makeSelectPostalCodeValidation,
  makeSelectPopStations,
  makeSelectPopStationDeliverySlots,
  makeSelectSimAddons,
  makeSelectFamilyPlanSims,
  makeSelectSimCount,
  makeSelectDeliveryLocation,
  makeSelectShowTransferNumberModalSelector,
  makeSelectSimType,
  makeSelectChannelReqOrderParams,
  makeSelectShow5GAddon,
  makeSelectShowSectionListModal,
  makeSelectShowDeviceCompatibilityChecker,
  makeSelectShow5GDeviceCompatibilityChecker,
  makeSelectPreselected5GAddon,
  makeSelectIs5GAddonSelected,
  makeSelectIs5GCompatible,
  makeSelectEnableNextButtonWithDeviceChecker,
  makeSelectHideComponentsWithDeviceChecker,
  makeSelectSelectedMobileDeviceBrand,
  makeSelectFilteredMobileDeviceModels,
  makeSelectSelectedMobileDevice,
  makeSelectDocTypes,
  makeSelectIs5GBundleAddonSelected
} from "ducks/selectors";
import validate, { asyncValidate } from "validations";
import styles from "./styles";
import SmartRender from "containers/SmartRender";
import { forms, SELF_ACT_DEL, paths, SIM_TYPES } from "utils/constants";
import Spinner, { styles as spinnerStyles } from "components/Spinner";

const SelfActivationDeliveryHOC = Component =>
  function Comp(props) {
    const {
      match,
      fetchPlan,
      isFetchingPlan,
      eligiblePlans,
      planDetails,
      isFetchingEligiblePlans,
      selectPlan,
      changeField,
      isEsimEnable
    } = props;
    useEffect(
      () => {
        if (
          isEmpty(planDetails) &&
          !isFetchingPlan &&
          !isFetchingEligiblePlans
        ) {
          if (isEmpty(eligiblePlans)) {
            fetchPlan(get(match, "params"));
          } else {
            const plan_name = urlParam("plan_name");
            const urlPlan = plan_name
              ? find(eligiblePlans, { name: plan_name })
              : undefined;
            const selectedPlan = !isEmpty(urlPlan)
              ? urlPlan
              : head(eligiblePlans);
            selectPlan(selectedPlan);
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [eligiblePlans, planDetails, isFetchingPlan, isFetchingEligiblePlans]
    );

    useEffect(() => {
      if (urlParam("number_type")) {
        changeField("number_type", urlParam("number_type"));
      }
    }, [changeField]);

    useEffect(() => {
      if (isEsimEnable && urlParam("sim_type") === SIM_TYPES.ESIM) {
        changeField("sim_type", SIM_TYPES.ESIM);
      }
    }, [isEsimEnable, changeField]);

    return isFetchingPlan ? (
      <Spinner size={200} sx={spinnerStyles.fullPage} />
    ) : (
      <Component {...props} />
    );
  };

export const mapStateToProps = createStructuredSelector({
  formValidations: makeSelectPageValidations(forms.SELF_ACTIVATION_FORM),
  formValues: makeSelectSelfActivationForm(),
  deliveryLocation: makeSelectDeliveryLocation(),
  addOns: makeSelectAddons(),
  defaultPlanAddons: makeSelectDefaultPlanAddons(),
  addons: makeSelectSelectedAddons(),
  referralCode: makeSelectReferralCode(),
  preSelectedOrderAddons: makeSelectPreSelectedOrderAddons(),
  planDetails: makeSelectPlanDetails(),
  payMonthlyItems: makeSelectPayMonthlyItems(),
  payTodayItems: makeSelectPayTodayItems(),
  defaultPromoCodeOffers: makeSelectDefaultPromoCodeOffers(),
  planName: makeSelectPlanName(),
  // planFAQ: makeSelectPlanFAQ(),
  orderDetails: makeSelectValidateIccId(),
  issuingStates: makeSelectStates(),
  orderParams: makeSelectSelfActivationOrderParams(),
  eligiblePlans: makeSelectEligiblePlans(),
  issuingCountries: makeSelectCountries(),
  topCountries: makeSelectCountries(),
  otherCountries: makeSelectOtherCountries(),
  primaryDocTypes: makeSelectPrimaryDocTypes(),
  docTypes: makeSelectDocTypes(),
  minimumAge: makeSelectMinimumAge(),
  monthOptions: makeSelectMonthOptions(),
  dateOptions: makeSelectDateOptions(),
  yearOptions: makeSelectYearOptions(),
  selectedNumber: makeSelectFormattedSelectedNumber(),
  deliveryMethods: makeSelectDeliveryMethods(),
  deliverySlotDates: makeSelectDeliveryDates(),
  combinedDeliverySlotDates: makeSelectCombinedDeliveryDates(),
  freeDeliverySlotTime: makeSelectFreeDeliverySlots(),
  specialDeliverySlotTime: makeSelectSpecialDeliverySlots(),
  postalCodeOptions: makeSelectPostalCodeOptions(),
  payload: makeSelectVerifyIdentityPayload(),
  selectedAddons: makeSelectConfirmedAddon(),
  selectedSimAddons: makeSelectSimAddons(),
  sim_count: makeSelectSimCount(),
  deliveryAddressError: makeSelectHasAddressError(
    forms.SELF_ACTIVATION_FORM,
    "delivery"
  ),
  isOrderCreating: makeSelectOrderCreationStatus(),
  defaultAddons: makeSelectPreSelectedAddons(),
  isFetchingPlan: makeSelectIsFetchingData("planService"),
  familyPlanSims: makeSelectFamilyPlanSims(),
  isFetchingEligiblePlans: makeSelectIsFetchingData("eligiblePlans"),
  skipPostalCodeValidation: () => true,
  postalCodeValidation: makeSelectPostalCodeValidation(),
  popStations: makeSelectPopStations(),
  popStationsDeliverySlots: makeSelectPopStationDeliverySlots(),
  isFetchingPopStations: makeSelectIsFetchingData("popStations"),
  styles: () => styles,
  initialValues: state => {
    return {
      ...makeSelectPersonalDetailsFormInitialValues()(state),
      delivery_method: {
        ...makeSelectDeliveryMethod()(state)
      },
      number_type: makeSelectNumberType()(state),
      ...makeSelectDetailsFromIccidVerification()(state),
      sim_type: makeSelectSimType()(state)
    };
  },
  isShowPromoCode: () => getURLParamValue("hpromo"),
  isShowAddOn: () => getURLParamValue("haddon"),
  isShowPlan: () => getURLParamValue("hplan"),
  hidePromo: () => getURLParamValue("hpromo"),
  hideAddon: () => getURLParamValue("haddon"),
  hideDeals: () => getURLParamValue("hdeals"),
  hidePlan: () => getURLParamValue("hplan"),
  hidePayToday: () => getURLParamValue("hpaytoday"),
  showTransferNumberModal: makeSelectShowTransferNumberModalSelector(),
  showSectionListModal: makeSelectShowSectionListModal(),
  isEsimEnable: () => getURLParamValue("esim"),
  show5GAddon: makeSelectShow5GAddon(),
  showDeviceCompatibilityChecker: makeSelectShowDeviceCompatibilityChecker(),
  show5GDeviceCompatibilityChecker: makeSelectShow5GDeviceCompatibilityChecker(),
  isFetchingDeliverySlots: makeSelectIsFetchingData("deliverySlots"),
  channelRequiredOrderParams: makeSelectChannelReqOrderParams(),
  preselected5GAddon: makeSelectPreselected5GAddon(),
  is5GAddonSelected: makeSelectIs5GAddonSelected(),
  is5GCompatible: makeSelectIs5GCompatible(),
  enableNextButtonWithDeviceChecker: makeSelectEnableNextButtonWithDeviceChecker(),
  hideComponentsWithDeviceChecker: makeSelectHideComponentsWithDeviceChecker(),
  selectedMobileDeviceBrand: makeSelectSelectedMobileDeviceBrand(),
  filteredMobileDeviceModels: makeSelectFilteredMobileDeviceModels(),
  selectedMobileDevice: makeSelectSelectedMobileDevice(),
  is5GBundleAddon: makeSelectIs5GBundleAddonSelected()
});

export const mapDispatchToProps = dispatch => {
  return {
    fetchPlan: params => dispatch(fetchPlan(params)),
    validateReferralCode: (referral_code, plan_name) =>
      dispatch(fetchReferralCode({ referral_code, plan_name })),
    resetReferralCode: () =>
      dispatch(clearData(["referralCode", "selectedAddons"])),
    selectPlan: plan => dispatch(selectPlan(plan)),
    validatePostalCode: (postal_code, field) => {
      dispatch(validatePostalCode({ postal_code }, field));
    },
    resetPostalCodeValidation: field => {
      dispatch(resetPostalCodeValidation(field));
    },
    setPostalCodeValidation: (field, data, loading) => {
      dispatch(setPostalCodeValidation(field, data, loading));
    },
    fetchAddressLookup: params => dispatch(fetchAddressLookup(params)),
    fetchDeliverySlots: params => {
      dispatch(fetchDeliverySlots({ ...params, channel: SELF_ACT_DEL }));
    },
    fetchPopStations: params => {
      dispatch(clearData("popStations"));
      dispatch(fetchPopStations(params));
    },
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.SELF_ACTIVATION_FORM,
          field: field,
          value: value
        })
      );
    },
    hideModal: () => {
      dispatch(
        setData({
          key: "showOrderInfoModal",
          data: { success: false, loading: false }
        })
      );
    },
    setSectionListModal: (value = false) => {
      dispatch(
        setData({
          key: "showSectionListModal",
          data: { enabled: value }
        })
      );
    },
    showHideTransferNumberModal: (flag = true) => {
      dispatch(
        setData({
          key: "showTransferNumberModal",
          data: { success: flag, loading: false }
        })
      );
    },
    resetDeliverySlots: () => dispatch(resetDeliverySlots()),
    createSelfActivationDeliveryOrder: (params, props) => {
      if (checkOrderParamsIsValid(params, props?.channelRequiredOrderParams)) {
        dispatch(
          createSelfActivationDeliveryOrder(
            params,
            ({ order_ref, plan_name }) => {
              if (order_ref) {
                setTimeout(() => {
                  gotoUrl(
                    `${paths.SELF_ACTIVATION_DELIVERY_SUCCESS}?order_ref=${order_ref}&plan_name=${plan_name}`,
                    false,
                    {},
                    window?.RELOAD_SAD_SUCCESS_PAGE
                  );
                }, 0);
              }
            }
          )
        );
      }
    },
    onSubmit: (navigate, payload, change) => {
      const {
        showOrderCreation,
        sim_type,
        self_activation_esim_enabled
      } = payload;
      const self_activation_esim_enabled_for_page =
        self_activation_esim_enabled &&
        [paths.CIRCLES1_DEALS, pathWithChannel(paths.PLANS)].includes(
          window.location.pathname
        );

      if (sim_type === "esim" && !self_activation_esim_enabled_for_page) {
        navigate();
        return;
      }

      if (!showOrderCreation) {
        dispatch(
          setData({
            key: "showOrderCreation",
            data: { success: true, loading: false }
          })
        );
        let element = head(document.getElementsByName("full_name"));
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      } else {
        dispatch(
          setData({
            key: "showOrderInfoModal",
            data: { success: true, loading: false }
          })
        );
      }
    },
    handleSelectAddon: (addonId, selected) => {
      return dispatch(
        setData({ key: "selectedAddons", data: { [addonId]: selected } })
      );
    },
    clearSelectAddon: addonId => {
      return dispatch(clearData("selectedAddons.data"));
    },
    clearDeliverySlots: () => dispatch(clearData("deliverySlots"))
  };
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  portInOnlyMode: get(stateProps, "referralCode.port_in_only"),
  appliedPromoCode: get(stateProps, "referralCode.valid"),
  messageData: {
    appliedPromoCode: get(stateProps, "referralCode.value"),
    data: get(stateProps, "planDetails.data"),
    voice: get(stateProps, "planDetails.voice"),
    sms: get(stateProps, "planDetails.sms"),
    family_plan_sims: get(stateProps, "familyPlanSims"),
    delivery_date: get(stateProps, "formValues.delivery_slot_full_data"),
    pop_station_outlet_name: `${get(
      stateProps,
      "formValues.pop_station_outlet_full_data.outlet_name",
      ""
    )}`,
    delivery_location: get(stateProps, "deliveryLocation"),
    selectedAddons: get(stateProps, "selectedAddons"),
    selectedSimAddons: get(stateProps, "selectedSimAddons"),
    familyPlanSimCount: get(stateProps, "sim_count"),
    referralCode: get(stateProps, "referralCode.value")
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.SELF_ACTIVATION_FORM,
    destroyOnUnmount: false,
    validate,
    asyncValidate,
    onSubmitFail: () => {
      autoScrollToError();
    }
  }),
  memo
)(SelfActivationDeliveryHOC(SmartRender));
